<template>
  <div>
    <k-dialog :title="$t('operationListComp.downloadReport')" @close="$emit('close')" class="reportPopup" width="600">
      <div class="body">
        <div v-if="loadingGrid" class="popup-loader">
          <GridLoading :infoText="$t('operationListComp.reportCompilationNotification')"></GridLoading>
        </div>
        <div v-else>
          <div>
            <div>
              <table class="table table-borderless" v-if="showQuantity || showProdLines">
                <tbody>
                  <tr>
                    <th class="InputTitle" v-if="showQuantity">{{ noOfGarmentsText }}</th>
                    <th class="InputTitle" v-if="showProdLines">{{ noOfProductLineText }}</th>
                  </tr>
                  <tr>
                    <td v-if="showQuantity">
                      <input
                        class="numberInput"
                        :class="{ errorStyle: errorMsgGarment }"
                        type="text"
                        inputmode="numeric"
                        pattern="[0-9]*"
                        id="tbox1"
                        name="tbox1"
                        @input="validateGarmentValue($event.target)"
                        v-model="noOfGarmentsVal"
                      />
                    </td>
                    <td v-if="showProdLines">
                      <NumberOfInput
                        class="noOfProductionLines"
                        @onChangeVal="validateProductionLineValue"
                        :error="errorMsgProductLine"
                        :Value="noOfProductLineVal"
                      ></NumberOfInput>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <label for="tbox1" style="padding-bottom: 6px; font-size: 14px; margin-right: 18rem; font-weight: bold">{{
              $t("operationListComp.chooseReport")
            }}</label>
            <div class="report-radio-group">
              <label>
                <input type="radio" value="sgc" v-model="selectedReport" />
                {{ $t("operationListComp.singleGarmentConsumption") }}
              </label>
              <label>
                <input type="radio" value="sgcWithQuantity" v-model="selectedReport" />
                {{ $t("operationListComp.singleGarmentConsumptionWithQuantity") }}
              </label>
              <label>
                <input type="radio" value="tu" v-model="selectedReport" />
                {{ $t("operationListComp.threadUsageReport") }}
              </label>
            </div>
            <div class="costElement">
              <label :class="{ checkedAndDisabled: disableCostElement }">
                <input type="checkbox" v-model="isChecked" :disabled="disableCostElement" />
                {{ $t("operationListComp.includeCostElement") }}
              </label>
            </div>
            <div>
              <label for="tbox1" style="padding-bottom: 6px; font-size: 14px; font-weight: bold">{{
                $t("operationListComp.chooseReportType")
              }}</label>
              <div class="reportFormat-radio-group">
                <label>
                  <input type="radio" value="pdf" v-model="selectedReportFormat" />
                  PDF <span class="far fa-file-pdf" style="padding-right: 5px"></span>
                </label>
                <label>
                  <input type="radio" value="csv" v-model="selectedReportFormat" />
                  CSV <span class="fas fa-file-csv" style="padding-right: 5px"></span>
                </label>
              </div>
            </div>
          </div>
          <dialog-actions-bar>
            <div class="popup-footer">
              <kbutton class="cancel" @click="$emit('close')"
                ><i class="fas fa-xmark" style="padding-right: 5px; font-weight: bold"></i
                >{{ $t("operationListComp.close") }}</kbutton
              >
              <kbutton class="btn-primary save" @click="handleReportDownload"
                ><i class="fas fa-download" style="padding-right: 5px"></i
                >{{ $t("operationListComp.download") }}</kbutton
              >
            </div>
          </dialog-actions-bar>
        </div>
      </div>
    </k-dialog>
  </div>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import { Dialog, DialogActionsBar } from "@progress/kendo-vue-dialogs";
import { Button } from "@progress/kendo-vue-buttons";
import GridLoading from "./GridLoading.vue";
import { useToast } from "vue-toastification";
import NumberOfInput from "./NumberOfInput.vue";

export default defineComponent({
  name: "ReportsPopup",
  props: {
    noOfGarments: {
      type: Number,
      required: true
    },
    noOfProductLine: {
      type: Number,
      required: true
    },
    selectedReportPrintingOption: {
      type: String,
      required: true
    },
    loadingGrid: {
      type: Boolean,
      required: true
    }
  },
  emits: [
    "close",
    "reportFormatChanged",
    "isChecked",
    "downloadSingleGarmentReport",
    "downloadSingleGarmentReportWithQuantity",
    "downloadThreadUsageReport",
    "noOfGarments",
    "noOfProductionLines"
  ],
  components: {
    "k-dialog": Dialog,
    "dialog-actions-bar": DialogActionsBar,
    kbutton: Button,
    GridLoading,
    NumberOfInput
  },
  data() {
    return {
      toast: useToast(),
      noOfGarmentsText: this.$t("editStyle.noOfGarments"),
      noOfProductLineText: this.$t("editStyle.noOfProductLine"),
      noOfGarmentsVal: this.noOfGarments,
      noOfProductLineVal: this.noOfProductLine,
      errorMsgGarment: "",
      errorMsgProductLine: "",
      selectedReport: "sgc" as any,
      selectedReportFormat: this.selectedReportPrintingOption,
      showQuantity: false,
      showProdLines: false,
      isChecked: false,
      disableCostElement: false
    };
  },
  watch: {
    selectedReport(newVal) {
      this.showQuantity = newVal !== "sgc";
      this.showProdLines = newVal !== "sgc" && newVal !== "sgcWithQuantity";
      this.disableCostElement = newVal === "tu" || newVal === "sgcWithQuantity";
      if (this.disableCostElement) {
        this.isChecked = true;
      } else {
        this.isChecked = false;
      }
    },
    selectedReportFormat(newVal) {
      localStorage.setItem("selectedReportPrintingOption", newVal);
      this.$emit("reportFormatChanged", newVal);
    }
  },
  methods: {
    handleReportDownload() {
      if (this.selectedReport === "sgcWithQuantity" && this.errorMsgGarment != "") {
        this.toast.error(this.errorMsgGarment, {
          toastClassName: "custom-error-toast",
          timeout: 5000
        });
        return;
      }
      if (this.selectedReport === "tu" && (this.errorMsgProductLine != "" || this.errorMsgGarment != "")) {
        if (this.errorMsgGarment != "") {
          this.toast.error(this.errorMsgGarment, {
            toastClassName: "custom-error-toast",
            timeout: 5000
          });
        }
        if (this.errorMsgProductLine != "") {
          this.toast.error(this.errorMsgProductLine, {
            toastClassName: "custom-error-toast",
            timeout: 5000
          });
        }
        return;
      }
      if (this.selectedReport === "sgc") {
        this.$emit("isChecked", this.isChecked);
        this.$emit("downloadSingleGarmentReport");
      } else if (this.selectedReport === "sgcWithQuantity" && this.errorMsgGarment === "") {
        this.$emit("isChecked", this.isChecked);
        this.$emit("noOfGarments", this.noOfGarmentsVal);
        this.$emit("downloadSingleGarmentReportWithQuantity");
      } else if (this.selectedReport === "tu" && this.errorMsgProductLine === "" && this.errorMsgGarment === "") {
        this.$emit("noOfGarments", this.noOfGarmentsVal);
        this.$emit("noOfProductionLines", this.noOfProductLineVal);
        this.$emit("downloadThreadUsageReport");
      }
    },
    validateGarmentValue(value: any): void {
      value = (value as HTMLInputElement).value.replaceAll(/\D/g, "");
      this.noOfGarmentsVal = value;
      if (value == "" || value == undefined || value == null)
        this.errorMsgGarment = this.$t("editStyle.pleaseEnterNoOfGarment") as string;
      else if (+value < 1) this.errorMsgGarment = this.$t("editStyle.valueShouldBeAtleast1") as string;
      else if (value.length > 7) {
        this.errorMsgGarment = this.$t("editStyle.maximum7CharactersAreAllowed") as string;
      } else this.errorMsgGarment = "";
      if (this.errorMsgGarment != "") {
        this.toast.error(this.errorMsgGarment, {
          toastClassName: "custom-error-toast",
          timeout: 5000
        });
      }
    },
    validateProductionLineValue(value: any): void {
      this.noOfProductLineVal = value;
      if (value == "" || value == undefined || value == null)
        this.errorMsgProductLine = this.$t("editStyle.pleaseEnterProductionLine") as string;
      else if (+value < 1) this.errorMsgProductLine = this.$t("editStyle.valueShouldBeAtleast1") as string;
      else if (value.length > 2) {
        this.errorMsgProductLine = this.$t("editStyle.maximum2CharactersAreAllowed") as string;
      } else this.errorMsgProductLine = "";
      if (this.errorMsgProductLine != "") {
        this.toast.error(this.errorMsgProductLine, {
          toastClassName: "custom-error-toast",
          timeout: 5000
        });
      }
    }
  }
});
</script>
<style scoped>
.save {
  color: #fff;
  background-color: #0054a6;
  border-radius: 5px;
  border: none;
}
.save:hover {
  background-color: #004080 !important;
  color: white;
}
.cancel {
  border-radius: 5px;
}
.numberInput {
  width: 100px !important;
  border-color: rgba(0, 0, 0, 0.08);
  color: #424242;
  background-color: #ffffff;
  border-radius: 2px;
  text-align: right;
  height: 28px;
  font-size: 15px;
}
.InputTitle {
  width: 150px;
  padding-bottom: 6px;
  font-weight: normal !important;
}
::v-deep(.numberInput .input) {
  width: 100px !important;
}
::v-deep(.noOfProductionLines .input) {
  width: 150px !important;
}
table {
  border-spacing: 0 !important;
  width: auto !important;
  margin-left: 0 !important;
  text-align: left;
  display: inline-block;
}

th,
td {
  vertical-align: top !important;
  margin: 0;
  width: auto !important;
  padding: 0 4px !important;
  text-align: left;
}

tr {
  vertical-align: top !important;
  padding: 0 !important;
  margin: 0;
}
.popup-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 5px;
}
::v-deep(.numberInput .textInput),
::v-deep(.noOfProductionLines .textInput) {
  height: auto !important;
  min-height: 0 !important;
  padding: 0 !important;
}
.report-radio-group {
  display: flex;
  flex-direction: column;
}
.reportFormat-radio-group {
  display: flex;
}
.costElement {
  margin-top: 10px;
}
.checkedAndDisabled {
  color: #888 !important;
}
.popup-loader {
  min-height: 250px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.popup-loader .text-center {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.popup-loader .text-info strong {
  margin-top: 16px;
  display: block;
}
</style>
<style>
.reportPopup .k-window-title::after {
  content: none;
}
.reportPopup .k-window-titlebar {
  background-color: #fff !important;
  color: black !important;
  margin-left: 5px;
}
.reportPopup .k-window-content {
  margin-left: 5px;
}
.reportPopup .k-dialog-buttongroup {
  border-top: 1px solid #e0e0e0 !important;
  box-shadow: none !important;
}
</style>
